import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {Card} from '@Components/event-wizard/card';
import {ClickableDiv} from '@Components/clickable-div';
import {
  useEventWizardEndDateTime,
  useEventWizardIsRecurring,
  useEventWizardRecurringData,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import {
  getFormattedDateForCard,
  getFormattedMonthForCard,
  getFormattedTimeForCard,
} from '@Components/event-wizard/event-wizard-date-time-summary/event-wizard-date-time-summary.helper';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {getDateTimeErrorText, getDateTimeErrorType} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.helper';
import {getGMTLabel} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {getRecurringEventDateText} from '@Libraries/events.library';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import styles from './event-wizard-date-time-summary.module.scss';
import type {EventWizardDateTimeSummaryProps} from './event-wizard-date-time-summary.types';

export function EventWizardDateTimeSummary({toggleExpandedState, isExpanded}: EventWizardDateTimeSummaryProps): ReactElement<any> {
  const startDateTime = useEventWizardStartDateTime();
  const endDateTime = useEventWizardEndDateTime();
  const isRecurring = useEventWizardIsRecurring();
  const recurringData = useEventWizardRecurringData();
  const timezone = useEventWizardTimeZone();

  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const isInErrorState = (): boolean => {
    return !!getDateTimeErrorType(startDateTime, endDateTime, timezone);
  };

  const getFirstCard = (useEndDateContentForMultiday = false): ReactElement<any> => {
    let title: string;
    let subtext: string | undefined;

    const multidayDateTime = useEndDateContentForMultiday ? endDateTime : startDateTime;

    if (isRecurring) {
      title = getRecurringEventDateText(recurringData);
    } else if (startDateTime.getDate() === endDateTime.getDate()) {
      title = getFormattedDateForCard(startDateTime);
    } else {
      title = getFormattedDateForCard(multidayDateTime);
      subtext = `${getFormattedTimeForCard(multidayDateTime)} (${getGMTLabel(timezone)})`;
    }

    return (
      <div className="spacing-p-r-3 _full-width _overflow-hidden">
        <Card
          customWidthClass={styles.cardWidth}
          text={title}
          subtext={subtext}
          textIcon={isRecurring ? 'icon-rotate-ccw' : ''}
          month={getFormattedMonthForCard(multidayDateTime)}
          date={multidayDateTime.getDate()}
        />
      </div>
    );
  };

  const getSecondCard = (): ReactElement<any> => {
    let title: string;
    const subtext = getGMTLabel(timezone);
    let thumbnailIconEl: ReactElement<any> | undefined;

    if (isSmallScreen && !isRecurring && startDateTime.getDate() !== endDateTime.getDate()) {
      return getFirstCard(true);
    }

    if (isRecurring || startDateTime.getDate() === endDateTime.getDate()) {
      title = `${getFormattedTimeForCard(startDateTime)} - ${getFormattedTimeForCard(endDateTime)}`;
      thumbnailIconEl = <Icon className="content-primary spacing-m-3" icon="icon-clock-o" size={IconSize.SIZE_ICON_16} />;
    } else {
      title = getFormattedDateForCard(endDateTime);
    }
    return <Card text={title} subtext={subtext} thumbnailIcon={thumbnailIconEl} />;
  };

  const getConnector = (): ReactElement<any> | undefined => {
    if (isRecurring || startDateTime.getDate() === endDateTime.getDate()) {
      return undefined;
    }

    if (isSmallScreen && startDateTime.getDate() !== endDateTime.getDate()) {
      return undefined;
    }

    return (
      <span className="spacing-m-l-4 spacing-m-r-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="9" viewBox="0 0 25 9" fill="none">
          <path
            d="M24.3536 4.85355C24.5488 4.65829 24.5488 4.34171 24.3536 4.14645L21.1716 0.964466C20.9763 0.769204 20.6597 0.769204 20.4645 0.964466C20.2692 1.15973 20.2692 1.47631 20.4645 1.67157L23.2929 4.5L20.4645 7.32843C20.2692 7.52369 20.2692 7.84027 20.4645 8.03553C20.6597 8.2308 20.9763 8.2308 21.1716 8.03553L24.3536 4.85355ZM0 5H24V4H0V5Z"
            fill="#CBCBD0"
          />
        </svg>
      </span>
    );
  };

  return (
    <div className={isExpanded ? '_hidden' : ''}>
      <ClickableDiv
        className={`${styles.summaryCard} ${isSmallScreen ? `${styles.gap12} flex-column-align-start` : 'flex-row-align-center'} justify-content-between spacing-p-2 ${isInErrorState() ? 'border-s-danger' : 'border-s-standard'} radius-8 _cursor-pointer ${styles.fadeIn}`}
        onClick={toggleExpandedState}
      >
        {getFirstCard()}
        {getConnector()}
        {getSecondCard()}
      </ClickableDiv>
      {isInErrorState() ? <EventWizardErrorInfo className="spacing-m-t-1" text={getDateTimeErrorText(startDateTime, endDateTime, timezone)} /> : undefined}
    </div>
  );
}
